import { useEffect } from 'react';
import { Alert, Checkbox, Form, Input, Modal, Typography } from 'antd';
import { REQUEST_TYPES, type DataSubjectRequest } from './api';

interface CompleteRequestModalProps {
  request: DataSubjectRequest | null;
  submitting: boolean;
  onSubmit: (note?: string) => void;
  onCancel: () => void;
}

interface FormValues {
  note?: string;
  confirmed?: boolean;
}

/**
 * Hoàn tất một yêu cầu.
 *
 * Với yêu cầu **xóa tài khoản**, bấm nút này là ẩn danh hồ sơ thật và **không lùi được**
 * (BR-804) — nên có thêm một ô tick xác nhận. Không phải để làm khó: đây là hàng đợi
 * người trực xử lý hàng loạt, và một cú bấm lệch dòng thì không có đường về.
 */
export function CompleteRequestModal({
  request,
  submitting,
  onSubmit,
  onCancel,
}: CompleteRequestModalProps) {
  const [form] = Form.useForm<FormValues>();

  useEffect(() => {
    if (request) form.setFieldsValue({ note: '', confirmed: false });
  }, [request, form]);

  const meta = request ? REQUEST_TYPES[request.type] : null;
  const irreversible = meta?.isIrreversible ?? false;

  return (
    <Modal
      open={request !== null}
      title={request ? `Hoàn tất: ${meta?.label} — ${request.memberCode}` : ''}
      okText={irreversible ? 'Xóa tài khoản' : 'Hoàn tất'}
      okButtonProps={irreversible ? { danger: true } : undefined}
      cancelText="Hủy"
      confirmLoading={submitting}
      onCancel={onCancel}
      destroyOnHidden
      onOk={() => {
        form
          .validateFields()
          .then((values) => onSubmit(values.note?.trim() || undefined))
          .catch(() => {});
      }}
    >
      {request && (
        <>
          {irreversible ? (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Thao tác này KHÔNG lùi được"
              description={
                <>
                  Hồ sơ của <strong>{request.memberName ?? request.memberCode}</strong> sẽ bị ẩn
                  danh: xóa họ tên, ngày sinh, số điện thoại và mọi thiết bị đã đăng ký. Sổ cái
                  điểm được giữ lại theo nghĩa vụ lưu trữ chứng từ tài chính, nhưng không còn gắn
                  với người nào.
                </>
              }
            />
          ) : (
            <Typography.Paragraph type="secondary">{meta?.description}</Typography.Paragraph>
          )}

          <Form form={form} layout="vertical" requiredMark={false}>
            <Form.Item
              name="note"
              label="Ghi chú xử lý"
              tooltip="Không bắt buộc, nhưng người đọc lại sau sáu tháng sẽ cần nó."
              rules={[{ max: 1000, message: 'Ghi chú quá dài.' }]}
            >
              <Input.TextArea rows={2} placeholder="Đã đối soát xong công nợ điểm của khách" />
            </Form.Item>

            {irreversible && (
              <Form.Item
                name="confirmed"
                valuePropName="checked"
                rules={[
                  {
                    // Tick sẵn thì ô này thành vô nghĩa; validator bắt phải tự chạm vào
                    validator: (_, value) =>
                      value === true
                        ? Promise.resolve()
                        : Promise.reject(new Error('Cần xác nhận trước khi xóa.')),
                  },
                ]}
              >
                <Checkbox>
                  Tôi đã đối soát và hiểu rằng thao tác này không lùi được
                </Checkbox>
              </Form.Item>
            )}
          </Form>
        </>
      )}
    </Modal>
  );
}
